// Web → Analytika → Prodejní stránka.
//
// Průchod stránkou shora dolů, sekci po sekci. Cíl je jediný: najít místo,
// kde text přestává držet, a to pak přepsat.
//
// U každé sekce vidíš celý řádek dat: kolik žen ji vidělo, za jak dlouho se
// k ní doscrollovaly, kolik času nad ní strávily, kolik jich právě tady
// kliklo směrem k objednávce, kolik jich nakonec zaplatilo a kolik jich tu
// stránku zavřelo.
//
// POZOR na jedno omezení: spojení „viděla sekci → koupila" drží jen v rámci
// jednoho dne. Otisk návštěvnice se každou půlnoc mění, takže kdo si to dnes
// přečte a koupí až zítra, spadne do statistiky jako dva různí lidé.
//
// 18.8.2026: název stránky je teď VŽDY nahoře, i když ještě nejsou data.
// Dřív se u prázdné stránky vykreslil bezejmenný box a žlutý rámeček té
// DRUHÉ stránky pak vypadal, že patří k němu. Odtud „analýza průchodu
// prodejkou nefunguje". Procenta jsou v pilulkách, ať se dají číst i na mobilu.
import React, { useMemo } from "react";
import { AlertTriangle, ArrowDown, Eye, LogOut, Lightbulb, Timer, MousePointerClick, ShoppingCart, Clock } from "lucide-react";

const BRAND = "#6E519F";
const pct = (a, b) => (b > 0 ? Math.round((a / b) * 1000) / 10 : 0);

const cas = (s) => {
  const n = Number(s) || 0;
  if (n <= 0) return null;
  if (n < 60) return `${n} s`;
  return `${Math.floor(n / 60)} m ${String(Math.round(n % 60)).padStart(2, "0")} s`;
};

// Kdy je propad mezi dvěma sousedními sekcemi ještě normální a kdy už problém.
// Prvních pár procent na každé sekci ztratí každá stránka, přes 20 % je signál.
const PRAH_VAROVNY = 12;
const PRAH_KRITICKY = 22;

function Delic() {
  return <span aria-hidden className="h-3 w-px shrink-0 bg-slate-300" />;
}

// Nadpis stránky. Drží se nahoře nad vším ostatním, ať je vždycky jasné,
// ke které stránce se čísla pod ním vztahují.
function Zahlavi({ nazev, zaklad, variantaPopis }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <h2 className="text-lg font-semibold text-slate-900">{nazev}</h2>
      {variantaPopis && (
        <span className="rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-semibold text-brand-800">
          {variantaPopis}
        </span>
      )}
      {zaklad > 0 && (
        <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-slate-600">
          {zaklad} návštěvnic
        </span>
      )}
    </div>
  );
}

export default function DetailProdejky({ poradi, sekce, odchody, celkem, nazev, variantaPopis }) {
  const zaklad = celkem || (Array.isArray(sekce) ? sekce[0]?.celkem : 0) || 0;

  const rows = useMemo(() => {
    if (!poradi?.length) return [];
    const mapaSekci = new Map((sekce || []).map((r) => [r.section, r]));
    const mapaOdchodu = new Map((odchody || []).map((r) => [r.sekce, r]));

    let predchozi = null;
    return poradi.map((p) => {
      const s = mapaSekci.get(p.key) || {};
      const o = mapaOdchodu.get(p.key) || {};
      const videlo = s.videlo || 0;
      const podil = pct(videlo, zaklad);
      const propad = predchozi == null ? 0 : Math.round((predchozi - podil) * 10) / 10;
      predchozi = podil;
      return {
        key: p.key,
        label: p.label,
        videlo,
        podil,
        propad,
        odchodu: o.odchodu || 0,
        dojiti: s.dojiti_s || 0,   // za jak dlouho se sem doscrollovaly
        stravily: s.cas_s || 0,    // kolik času nad sekcí strávily
        kliklo: s.kliklo || 0,     // kolik jich tu kliklo k objednávce
        koupilo: s.koupilo || 0,   // kolik z nich nakonec zaplatilo
      };
    });
  }, [poradi, sekce, odchody, zaklad]);

  const maData = rows.some((r) => r.videlo > 0);

  const problemy = useMemo(
    () => rows.filter((r) => r.propad >= PRAH_VAROVNY).sort((a, b) => b.propad - a.propad).slice(0, 3),
    [rows]
  );

  // Sekce, u které padlo nejvíc kliknutí k objednávce. To je místo, které prodává.
  const nejlepsi = useMemo(() => {
    const s = rows.filter((r) => r.kliklo > 0).sort((a, b) => b.kliklo - a.kliklo)[0];
    return s || null;
  }, [rows]);

  if (!maData) {
    return (
      <div className="space-y-3">
        <Zahlavi nazev={nazev} zaklad={0} variantaPopis={variantaPopis} />
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
          {variantaPopis
            ? `Za variantu ${variantaPopis} tu v tomhle období není nic naměřeno. Zkuste přepnout variantu nebo prodloužit období.`
            : "Zatím žádná data z této stránky. Objeví se, jakmile ji někdo otevře."}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Zahlavi nazev={nazev} zaklad={zaklad} variantaPopis={variantaPopis} />

      {/* -------------------------------------------------- kde je problém */}
      {!!problemy.length && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:p-5">
          <h3 className="mb-2 flex items-center gap-2 text-base font-semibold text-amber-900">
            <AlertTriangle className="w-4 h-4 shrink-0" /> Kde stránka ztrácí nejvíc
          </h3>
          <ul className="space-y-1.5">
            {problemy.map((p) => (
              <li key={p.key} className="flex flex-wrap items-center gap-2 text-sm text-amber-900">
                <strong>{p.label}</strong>
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-amber-800">
                  ubylo {p.propad} %
                </span>
                {p.odchodu > 0 && (
                  <span className="rounded-full bg-white/70 px-2 py-0.5 text-xs font-medium tabular-nums text-amber-800">
                    {p.odchodu} tu web zavřelo
                  </span>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-amber-800">
            <Lightbulb className="mt-0.5 w-3.5 h-3.5 shrink-0" />
            Sekci s největším propadem přepište první. Buď je moc dlouhá, nebo v ní
            čtenářka nenajde odpověď na otázku, kterou má právě v hlavě.
          </p>
        </div>
      )}

      {/* -------------------------------------------- co naopak funguje */}
      {nejlepsi && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <span>
            Nejvíc kliknutí k objednávce padá po sekci <strong>{nejlepsi.label}</strong>
          </span>
          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-emerald-800">
            {nejlepsi.kliklo} kliknutí
          </span>
          {nejlepsi.koupilo > 0 && (
            <span className="rounded-full bg-white/70 px-2 py-0.5 text-xs font-semibold tabular-nums text-emerald-800">
              {nejlepsi.koupilo} zaplatilo
            </span>
          )}
        </div>
      )}

      {/* ------------------------------------------- průchod stránkou dolů */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
        <h3 className="mb-1 text-base font-semibold text-slate-900">Průchod stránkou shora dolů</h3>
        <p className="mb-4 text-xs leading-relaxed text-slate-500">
          Sekce jsou v pořadí, jak jdou na stránce. Pilulka vpravo je podíl žen, které sekci
          opravdu viděly.
        </p>

        <ol className="space-y-1">
          {rows.map((r, i) => {
            const kriticky = r.propad >= PRAH_KRITICKY;
            const varovny = !kriticky && r.propad >= PRAH_VAROVNY;
            const barva = kriticky ? "#e11d48" : varovny ? "#f59e0b" : BRAND;

            return (
              <li key={r.key}>
                {/* spojnice mezi sekcemi s propadem */}
                {i > 0 && (
                  <div className="flex items-center gap-2 py-1 pl-3 text-xs">
                    <ArrowDown className="w-3.5 h-3.5 shrink-0 text-slate-300" />
                    {r.propad > 0 ? (
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums ${
                          kriticky
                            ? "bg-rose-50 text-rose-700"
                            : varovny
                            ? "bg-amber-50 text-amber-700"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        ubylo {r.propad} %
                      </span>
                    ) : (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-400">
                        beze ztráty
                      </span>
                    )}
                  </div>
                )}

                <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="font-medium text-slate-900">
                      <span className="mr-2 text-xs text-slate-400">{i + 1}.</span>
                      {r.label}
                    </span>
                    <span className="rounded-full bg-white px-2.5 py-0.5 text-xs font-semibold tabular-nums text-slate-700 ring-1 ring-slate-200">
                      {r.podil} %
                    </span>
                  </div>

                  <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${Math.max(r.podil, r.videlo > 0 ? 1.5 : 0)}%`, backgroundColor: barva }}
                    />
                  </div>

                  {/* celý řádek dat k sekci */}
                  <div className="mt-2.5 flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-xs text-slate-600">
                    <span className="inline-flex items-center gap-1 tabular-nums" title="Kolik žen sekci vidělo">
                      <Eye className="w-3.5 h-3.5 text-slate-400" /> {r.videlo}
                    </span>

                    {r.dojiti > 0 && (
                      <>
                        <Delic />
                        <span className="inline-flex items-center gap-1 tabular-nums" title="Za jak dlouho od otevření stránky se sem doscrollovaly">
                          <Clock className="w-3.5 h-3.5 text-slate-400" /> dojdou za {cas(r.dojiti)}
                        </span>
                      </>
                    )}

                    {r.stravily > 0 && (
                      <>
                        <Delic />
                        <span className="inline-flex items-center gap-1 tabular-nums" title="Kolik času měly sekci před očima">
                          <Timer className="w-3.5 h-3.5 text-slate-400" /> čtou {cas(Math.round(r.stravily))}
                        </span>
                      </>
                    )}

                    {r.kliklo > 0 && (
                      <>
                        <Delic />
                        <span className="inline-flex items-center gap-1 tabular-nums font-medium text-brand-700" title="Kolik žen odsud kliklo směrem k objednávce">
                          <MousePointerClick className="w-3.5 h-3.5" /> {r.kliklo} kliklo
                        </span>
                      </>
                    )}

                    {r.koupilo > 0 && (
                      <>
                        <Delic />
                        <span className="inline-flex items-center gap-1 tabular-nums font-semibold text-emerald-700" title="Kolik z těch, které sekci viděly, nakonec zaplatilo">
                          <ShoppingCart className="w-3.5 h-3.5" /> {r.koupilo} koupilo
                          <span className="font-normal text-emerald-600">({pct(r.koupilo, r.videlo)} %)</span>
                        </span>
                      </>
                    )}

                    {r.odchodu > 0 && (
                      <>
                        <Delic />
                        <span className="inline-flex items-center gap-1 tabular-nums text-rose-600" title="Kolik žen zavřelo stránku právě tady">
                          <LogOut className="w-3.5 h-3.5" /> {r.odchodu} odešlo
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>

        <p className="mt-4 text-xs leading-relaxed text-slate-500">
          Údaj „koupilo" spojuje ženu, která sekci viděla, s tím, že pak zaplatila. Drží to
          jen v rámci jednoho dne, protože otisk návštěvnice se každou půlnoc mění.
        </p>
      </div>
    </div>
  );
}
